/**
 * DH Commerce Offers Engine
 * Framework baseline: v1.4
 *
 * Handles configuration-driven storefront offers:
 * - promotional messages
 * - cart thresholds
 * - free-gift qualification
 * - upsell recommendations
 * - regional offers
 * - query-parameter campaigns
 *
 * Requires:
 *   dh-commerce-rules.js
 *
 * Load this file after dh-commerce-rules.js.
 */

(function (window, document) {
  "use strict";

  var DHCommerce = window.DHCommerce = window.DHCommerce || {};

  if (!DHCommerce.ruleEngine) {
    console.warn(
      "DH Commerce Offers: dh-commerce-rules.js must load before dh-commerce-offers.js."
    );
    return;
  }

  DHCommerce.state = DHCommerce.state || {};
  DHCommerce.offers = DHCommerce.offers || [];
  DHCommerce.offerEngine = DHCommerce.offerEngine || {};

  function asArray(value) {
    if (value == null) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  function clone(value) {
    if (value == null) {
      return value;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return value;
    }
  }

  function normalizeMoney(value) {
    var number = Number(value);

    if (!Number.isFinite(number)) {
      return 0;
    }

    return number;
  }

  function getCart(context) {
    var state = context || DHCommerce.state || {};

    return state.cart || {};
  }

  function getCartSubtotal(context) {
    var state = context || DHCommerce.state || {};
    var cart = getCart(state);

    if (cart.subtotal != null) {
      return normalizeMoney(cart.subtotal);
    }

    if (cart.total_price != null) {
      return normalizeMoney(cart.total_price) / 100;
    }

    if (state.cartSubtotal != null) {
      return normalizeMoney(state.cartSubtotal);
    }

    return 0;
  }

  function getCartQuantity(context) {
    var cart = getCart(context);

    if (cart.item_count != null) {
      return Number(cart.item_count) || 0;
    }

    if (cart.quantity != null) {
      return Number(cart.quantity) || 0;
    }

    if (Array.isArray(cart.items)) {
      return cart.items.reduce(function (total, item) {
        return total + (Number(item.quantity) || 0);
      }, 0);
    }

    return 0;
  }

  function getCartItems(context) {
    var cart = getCart(context);

    return Array.isArray(cart.items) ? cart.items : [];
  }

  function cartContains(context, match) {
    var items = getCartItems(context);

    return items.some(function (item) {
      if (match.variantId != null) {
        return String(item.variant_id || item.variantId) ===
          String(match.variantId);
      }

      if (match.productId != null) {
        return String(item.product_id || item.productId) ===
          String(match.productId);
      }

      if (match.handle) {
        return String(
          item.handle || item.product_handle || ""
        ) === String(match.handle);
      }

      if (match.tag) {
        var tags = asArray(item.tags).map(function (tag) {
          return String(tag).toLowerCase();
        });

        return tags.indexOf(
          String(match.tag).toLowerCase()
        ) !== -1;
      }

      if (match.productType) {
        return String(
          item.product_type || item.productType || ""
        ).toLowerCase() ===
          String(match.productType).toLowerCase();
      }

      return false;
    });
  }

  function dispatch(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: detail || {}
      })
    );
  }

  function findOffer(id) {
    return DHCommerce.offers.find(function (offer) {
      return offer.id === id;
    }) || null;
  }

  function normalizeOffer(offer) {
    var normalized = clone(offer) || {};

    normalized.id = String(normalized.id || "");
    normalized.enabled = normalized.enabled !== false;
    normalized.priority = Number(normalized.priority || 0);
    normalized.type = normalized.type || "message";
    normalized.actions = asArray(normalized.actions);

    return normalized;
  }

  function buildRuleFromOffer(offer) {
    return {
      id: "offer:" + offer.id,
      enabled: offer.enabled,
      priority: offer.priority,
      when: offer.when || null,
      then: [
        {
          type: "activateOffer",
          offerId: offer.id
        }
      ]
    };
  }

  DHCommerce.actions.activateOffer = function (
    action,
    context,
    rule
  ) {
    var offer = findOffer(action.offerId);

    if (!offer || offer.enabled === false) {
      return;
    }

    var payload = {
      offer: clone(offer),
      context: context || DHCommerce.state || {},
      rule: rule || null,
      timestamp: new Date().toISOString()
    };

    dispatch("dhcommerce:offer-activated", payload);

    if (offer.type) {
      dispatch(
        "dhcommerce:offer-" + offer.type,
        payload
      );
    }

    asArray(offer.actions).forEach(
      function (offerAction) {
        var handler =
          DHCommerce.actions[offerAction.type];

        if (typeof handler !== "function") {
          console.warn(
            "DH Commerce Offers: No action handler registered for",
            offerAction.type
          );
          return;
        }

        handler(
          offerAction,
          context || DHCommerce.state || {},
          rule
        );
      }
    );

    return payload;
  };

  DHCommerce.actions.showOfferMessage = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element) {
      return;
    }

    element.textContent = action.message || "";
    element.hidden = false;
    element.setAttribute(
      "aria-hidden",
      "false"
    );

    if (action.className) {
      element.classList.add(action.className);
    }
  };

  DHCommerce.actions.hideOfferMessage = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element) {
      return;
    }

    element.hidden = true;
    element.setAttribute(
      "aria-hidden",
      "true"
    );
  };

  DHCommerce.actions.setOfferData = function (
    action
  ) {
    var element = document.querySelector(
      action.selector
    );

    if (!element || !action.name) {
      return;
    }

    element.dataset[action.name] =
      action.value == null
        ? ""
        : String(action.value);
  };

  DHCommerce.actions.recommendProduct = function (
    action,
    context,
    rule
  ) {
    dispatch("dhcommerce:recommend-product", {
      product: clone(action.product || {}),
      placement: action.placement || "default",
      context: context || DHCommerce.state || {},
      rule: rule || null
    });
  };

  DHCommerce.actions.qualifyGift = function (
    action,
    context,
    rule
  ) {
    dispatch("dhcommerce:gift-qualified", {
      gift: clone(action.gift || {}),
      message: action.message || "",
      context: context || DHCommerce.state || {},
      rule: rule || null
    });
  };

  DHCommerce.actions.disqualifyGift = function (
    action,
    context,
    rule
  ) {
    dispatch("dhcommerce:gift-disqualified", {
      gift: clone(action.gift || {}),
      message: action.message || "",
      context: context || DHCommerce.state || {},
      rule: rule || null
    });
  };

  DHCommerce.conditions.cartSubtotal = function (
    condition,
    context
  ) {
    var actual = getCartSubtotal(context);
    var expected = normalizeMoney(condition.value);
    var operator = condition.operator || "gte";

    switch (operator) {
      case "gt":
        return actual > expected;
      case "gte":
        return actual >= expected;
      case "lt":
        return actual < expected;
      case "lte":
        return actual <= expected;
      case "eq":
        return actual === expected;
      case "neq":
        return actual !== expected;
      default:
        return false;
    }
  };

  DHCommerce.conditions.cartQuantity = function (
    condition,
    context
  ) {
    var actual = getCartQuantity(context);
    var expected = Number(condition.value) || 0;
    var operator = condition.operator || "gte";

    switch (operator) {
      case "gt":
        return actual > expected;
      case "gte":
        return actual >= expected;
      case "lt":
        return actual < expected;
      case "lte":
        return actual <= expected;
      case "eq":
        return actual === expected;
      case "neq":
        return actual !== expected;
      default:
        return false;
    }
  };

  DHCommerce.conditions.cartContains = function (
    condition,
    context
  ) {
    return cartContains(context, condition);
  };

  DHCommerce.conditions.customerRegion = function (
    condition,
    context
  ) {
    var state = context || DHCommerce.state || {};
    var region =
      state.region ||
      (state.customer && state.customer.region) ||
      (state.location && state.location.region) ||
      "";

    var expected = asArray(condition.value).map(
      function (value) {
        return String(value).toLowerCase();
      }
    );

    return expected.indexOf(
      String(region).toLowerCase()
    ) !== -1;
  };

  DHCommerce.offerEngine = {
    register: function (offer) {
      var normalized = normalizeOffer(offer);

      if (!normalized.id) {
        console.warn(
          "DH Commerce Offers: Offer requires an id."
        );
        return null;
      }

      var existingIndex =
        DHCommerce.offers.findIndex(
          function (item) {
            return item.id === normalized.id;
          }
        );

      if (existingIndex >= 0) {
        DHCommerce.offers[existingIndex] =
          normalized;
      } else {
        DHCommerce.offers.push(normalized);
      }

      DHCommerce.ruleEngine.register(
        buildRuleFromOffer(normalized)
      );

      dispatch("dhcommerce:offer-registered", {
        offer: clone(normalized)
      });

      return normalized;
    },

    registerMany: function (offers) {
      return asArray(offers)
        .map(function (offer) {
          return DHCommerce.offerEngine.register(
            offer
          );
        })
        .filter(Boolean);
    },

    remove: function (offerId) {
      DHCommerce.offers =
        DHCommerce.offers.filter(
          function (offer) {
            return offer.id !== offerId;
          }
        );

      DHCommerce.ruleEngine.remove(
        "offer:" + offerId
      );

      dispatch("dhcommerce:offer-removed", {
        offerId: offerId
      });
    },

    get: function (offerId) {
      return findOffer(offerId);
    },

    getAll: function () {
      return DHCommerce.offers.slice();
    },

    evaluate: function (context) {
      return DHCommerce.ruleEngine.evaluate(
        context || DHCommerce.state || {}
      );
    },

    test: function (offer, context) {
      var normalized = normalizeOffer(offer);

      return DHCommerce.ruleEngine.test(
        buildRuleFromOffer(normalized),
        context || DHCommerce.state || {}
      );
    },

    clear: function () {
      DHCommerce.offers.slice().forEach(
        function (offer) {
          DHCommerce.ruleEngine.remove(
            "offer:" + offer.id
          );
        }
      );

      DHCommerce.offers = [];

      dispatch("dhcommerce:offers-cleared");
    }
  };

  document.dispatchEvent(
    new CustomEvent(
      "dhcommerce:offer-engine-ready",
      {
        detail: {
          version: "1.4"
        }
      }
    )
  );

})(window, document);